/**
 * Inline edit mode for admin-managed page content.
 *
 * The provider is mounted app-wide but is inert until the URL carries
 * `?edit=1`. Two ways it can turn on:
 *
 *  - `?studio=<token>` — the page is the preview pane inside the Store Editor.
 *    The Admin runs on a different host, so a short-lived preview token is the
 *    credential. It is read-only: it unlocks the editing overlay and the
 *    current drafts, never a save or a publish.
 *  - `?edit=1` on its own — a signed-in admin browsing the storefront directly.
 */
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  isAllowedOrigin,
  isStudioMessage,
  parentTargetOrigin,
  PREVIEW_SOURCE,
} from "@/lib/cms/studio-bridge";
import { exchangePreviewSession } from "@/lib/cms.functions";
import { newBlock, type Block, type BlockData, type BlockType, type PageDocument } from "@/lib/cms/blocks";

const EditorDock = lazy(() => import("@/components/cms/EditorDock"));

export type Registered = { path: string; label: string; doc: PageDocument };

type Ctx = {
  active: boolean;
  /** Drafts handed over by the Store Editor preview session, by path. */
  sessionDrafts: Record<string, PageDocument> | null;
  regions: Record<string, Registered>;
  selected: { path: string; id: string } | null;
  register: (path: string, label: string, doc: PageDocument) => void;
  setDoc: (path: string, doc: PageDocument) => void;
  select: (sel: { path: string; id: string } | null) => void;
  addBlock: (path: string, type: BlockType, index?: number) => void;
  moveBlock: (path: string, id: string, dir: -1 | 1) => void;
  removeBlock: (path: string, id: string) => void;
  duplicateBlock: (path: string, id: string) => void;
  updateBlock: (path: string, id: string, data: BlockData) => void;
  markClean: (path: string) => void;
  dirty: Record<string, boolean>;
};

const CmsEditContext = createContext<Ctx | null>(null);

export function useCmsEdit(): Ctx {
  const ctx = useContext(CmsEditContext);
  if (!ctx) {
    throw new Error("useCmsEdit must be used inside CmsEditProvider");
  }
  return ctx;
}

export function CmsEditProvider({ children }: { children: ReactNode }) {
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>;
  // Fall back to the raw URL: some routes normalise their search params, and a
  // dropped `studio` token would silently downgrade the preview to the
  // stand-alone dock (which never completes the editor handshake).
  const urlParams =
    typeof window === "undefined" ? null : new URLSearchParams(window.location.search);
  const wantsEdit =
    String(search?.["edit"] ?? "") === "1" || urlParams?.get("edit") === "1";
  const studioToken = String(search?.["studio"] ?? "") || (urlParams?.get("studio") ?? "");
  const exchange = useServerFn(exchangePreviewSession);

  // We are the preview pane when a studio token is present and we are framed.
  const framed = typeof window !== "undefined" && window.parent !== window;
  const inStudio = wantsEdit && studioToken.length > 20 && framed;


  const [isAdmin, setIsAdmin] = useState(false);
  const [session, setSession] = useState<{
    sid: string;
    canPublish: boolean;
    drafts: Record<string, PageDocument>;
  } | null>(null);
  const active = wantsEdit && (isAdmin || Boolean(session));

  /* --- Preview session (cross-host) --- */
  useEffect(() => {
    if (!inStudio) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await exchange({ data: { token: studioToken } });
        if (cancelled) return;
        if (res.ok) {
          setSession({ sid: res.sid, canPublish: res.canPublish, drafts: res.drafts });
        } else if (window.parent !== window) {
          window.parent.postMessage(
            { source: PREVIEW_SOURCE, sid: "", type: "error", message: res.error ?? "Preview session rejected" },
            parentTargetOrigin(),
          );
        }
      } catch {
        if (!cancelled && window.parent !== window) {
          window.parent.postMessage(
            { source: PREVIEW_SOURCE, sid: "", type: "error", message: "Could not start the preview session" },
            parentTargetOrigin(),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inStudio, studioToken, exchange]);

  /* --- Same-host admin browsing with ?edit=1 --- */
  useEffect(() => {
    if (!wantsEdit || inStudio) return;
    let cancelled = false;
    void (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: userRes } = await supabase.auth.getUser();
        const uid = userRes.user?.id;
        if (!uid) return;
        const { data } = await supabase.rpc("cms_can_edit", { _user_id: uid });
        if (!cancelled && data === true) setIsAdmin(true);
      } catch {
        /* not allowed — stay inert */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wantsEdit, inStudio]);

  const [regions, setRegions] = useState<Record<string, Registered>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<{ path: string; id: string } | null>(null);

  const register = useCallback((path: string, label: string, doc: PageDocument) => {
    setRegions((prev) => {
      const cur = prev[path];
      // Keep whatever is already being edited; only fill in an area that is
      // still empty (its content may arrive a moment after it first renders).
      if (cur && (cur.doc.blocks.length > 0 || doc.blocks.length === 0)) return prev;
      return { ...prev, [path]: { path, label, doc } };
    });
  }, []);

  const setDoc = useCallback((path: string, doc: PageDocument) => {
    setRegions((prev) => ({ ...prev, [path]: { ...(prev[path] ?? { path, label: path }), doc } }));
    setDirty((prev) => ({ ...prev, [path]: true }));
  }, []);

  const mutate = useCallback(
    (path: string, fn: (blocks: Block[]) => Block[]) => {
      setRegions((prev) => {
        const cur = prev[path];
        if (!cur) return prev;
        return { ...prev, [path]: { ...cur, doc: { blocks: fn(cur.doc.blocks) } } };
      });
      setDirty((prev) => ({ ...prev, [path]: true }));
    },
    [],
  );

  const addBlock = useCallback(
    (path: string, type: BlockType, index?: number) => {
      const block = newBlock(type);
      mutate(path, (blocks) => {
        const next = [...blocks];
        next.splice(index ?? next.length, 0, block);
        return next;
      });
      setSelected({ path, id: block.id });
    },
    [mutate],
  );

  const moveBlock = useCallback(
    (path: string, id: string, dir: -1 | 1) => {
      mutate(path, (blocks) => {
        const i = blocks.findIndex((b) => b.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= blocks.length) return blocks;
        const next = [...blocks];
        const [moved] = next.splice(i, 1);
        next.splice(j, 0, moved!);
        return next;
      });
    },
    [mutate],
  );

  const removeBlock = useCallback(
    (path: string, id: string) => {
      mutate(path, (blocks) => blocks.filter((b) => b.id !== id));
      setSelected((cur) => (cur && cur.id === id ? null : cur));
    },
    [mutate],
  );

  const duplicateBlock = useCallback(
    (path: string, id: string) => {
      mutate(path, (blocks) => {
        const i = blocks.findIndex((b) => b.id === id);
        if (i < 0) return blocks;
        const copy: Block = {
          ...blocks[i]!,
          id: `b_${Math.random().toString(36).slice(2, 10)}`,
          data: JSON.parse(JSON.stringify(blocks[i]!.data)) as BlockData,
        };
        const next = [...blocks];
        next.splice(i + 1, 0, copy);
        return next;
      });
    },
    [mutate],
  );

  const updateBlock = useCallback(
    (path: string, id: string, data: BlockData) => {
      mutate(path, (blocks) => blocks.map((b) => (b.id === id ? { ...b, data } : b)));
    },
    [mutate],
  );

  const markClean = useCallback((path: string) => {
    setDirty((prev) => ({ ...prev, [path]: false }));
  }, []);

  /* ---------------- Store Editor bridge (preview side) ---------------- */

  const sid = session?.sid ?? "";
  const post = useCallback(
    (msg: Record<string, unknown>) => {
      if (typeof window === "undefined" || window.parent === window) return;
      window.parent.postMessage({ source: PREVIEW_SOURCE, sid, ...msg }, parentTargetOrigin());
    },
    [sid],
  );

  // Content pushed down from the editor panel. Applied without marking the
  // region dirty: the panel owns saving.
  useEffect(() => {
    if (!inStudio || !active || !sid) return;
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window.parent) return;
      if (!isAllowedOrigin(e.origin)) return;
      const msg = e.data as unknown;
      if (!isStudioMessage(msg)) return;
      if (msg.sid !== sid) return;
      if (msg.type === "ping") {
        post({ type: "ready", canPublish: session?.canPublish ?? false });
      }
      if (msg.type === "docs") {
        setRegions((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const [path, doc] of Object.entries(msg.docs)) {
            const cur = next[path];
            if (!cur || cur.doc === doc) continue;
            next[path] = { ...cur, doc };
            changed = true;
          }
          return changed ? next : prev;
        });
      }
      if (msg.type === "select") {
        setSelected(msg.id ? { path: msg.path, id: msg.id } : null);
        if (msg.id) {
          const el = document.querySelector(`[data-cms-block="${msg.id}"]`);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    };
    window.addEventListener("message", onMsg);
    post({ type: "ready", canPublish: session?.canPublish ?? false });
    return () => window.removeEventListener("message", onMsg);
  }, [inStudio, active, sid, post, session?.canPublish]);

  // Tell the panel which editable areas this page has, and what is in them.
  // Sent on every change (including the very first, empty, one) so the outline
  // fills in as soon as the page renders.
  const lastRegions = useRef("");
  useEffect(() => {
    if (!inStudio || !active || !sid) return;
    const payload = Object.values(regions).map((r) => ({
      path: r.path,
      label: r.label,
      doc: r.doc,
    }));
    const key = JSON.stringify(payload.map((r) => [r.path, r.doc.blocks.length]));
    lastRegions.current = key;
    post({ type: "regions", regions: payload });
  }, [inStudio, active, sid, regions, post]);

  const select = useCallback(
    (sel: { path: string; id: string } | null) => {
      setSelected(sel);
      if (inStudio && sel) post({ type: "select", path: sel.path, id: sel.id });
    },
    [inStudio, post],
  );

  const value = useMemo<Ctx>(
    () => ({
      active,
      sessionDrafts: session?.drafts ?? null,
      regions,
      selected,
      register,
      setDoc,
      select,
      addBlock,
      moveBlock,
      removeBlock,
      duplicateBlock,
      updateBlock,
      markClean,
      dirty,
    }),
    [
      active,
      session,
      regions,
      selected,
      register,
      setDoc,
      select,
      addBlock,
      moveBlock,
      removeBlock,
      duplicateBlock,
      updateBlock,
      markClean,
      dirty,
    ],
  );

  return (
    <CmsEditContext.Provider value={value}>
      {children}
      {active && !inStudio && !framed ? (
        <Suspense fallback={null}>
          <EditorDock />
        </Suspense>
      ) : null}

    </CmsEditContext.Provider>
  );
}
